import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from '@parcel/ui';
import {
  ArrowUpRight,
  FileText,
  Gavel,
  Globe,
  Landmark,
  type LucideIcon,
  Mail,
  Megaphone,
  MessagesSquare,
  Newspaper,
  Presentation,
  Quote,
  Video,
} from 'lucide-react';
import * as React from 'react';
import type { ApiProjectDocument } from '../../lib/projects.js';
import { formatSignalCalendarDate } from '../../lib/signal-dates.js';
import { ListSkeleton } from '../entity-drawer/detail-skeletons.js';
import { EmptyTabState } from '../entity-drawer/entity-drawer-sections.js';

// Web pages carry a class, not a title; a filing's type is already readable.
const WEB_DOCUMENT_TYPES: Readonly<Record<string, string>> = {
  government_page: 'Government page',
  news: 'News',
  press_release: 'Press release',
  firm_site: "Firm's site",
  official_project_site: 'Project site',
};

const WEB_DOCUMENT_ICONS: Readonly<Record<string, LucideIcon>> = {
  government_page: Landmark,
  news: Newspaper,
  press_release: Megaphone,
  firm_site: Globe,
  official_project_site: Globe,
};

// Filing types are free text from each city, so the icon is matched on words in the title.
const FILING_ICONS: ReadonlyArray<[RegExp, LucideIcon]> = [
  [/video|recording/i, Video],
  [/presentation|slides|deck/i, Presentation],
  [/comment|testimony|correspondence/i, MessagesSquare],
  [/letter|loi|notice/i, Mail],
  [/board|decision|determination|vote|approval|hearing/i, Gavel],
  [/news/i, Newspaper],
];

function documentIcon(type: string): LucideIcon {
  return (
    WEB_DOCUMENT_ICONS[type] ??
    FILING_ICONS.find(([pattern]) => pattern.test(type))?.[1] ??
    FileText
  );
}

function isWebUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

function yearOf(doc: ApiProjectDocument): string {
  return doc.document_date ? doc.document_date.slice(0, 4) : 'Undated';
}

/** The documents Parcel holds for a project, grouped by year; images live in the cover, never here. */
export function ProjectDocumentsTab({
  documents,
  isLoading,
  isError = false,
  hasMore,
  onLoadMore,
}: {
  documents: ApiProjectDocument[];
  isLoading: boolean;
  isError?: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}) {
  if (documents.length === 0) {
    if (isLoading) return <ListSkeleton plain />;
    // A failed request is not an empty trail, so it never says nothing is on file.
    return (
      <EmptyTabState message={isError ? 'Documents could not be loaded' : 'No documents on file'} />
    );
  }

  // The API returns newest first, so groups come out in year order.
  const groups: Array<{ year: string; docs: ApiProjectDocument[] }> = [];
  for (const doc of documents) {
    const year = yearOf(doc);
    const last = groups.at(-1);
    if (last?.year === year) last.docs.push(doc);
    else groups.push({ year, docs: [doc] });
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-5">
        {groups.map((group) => (
          <section key={group.year} className="space-y-1.5">
            <div className="flex items-center gap-3 text-xs font-medium text-muted-foreground">
              <span>{group.year}</span>
              <span className="h-px flex-1 bg-border" aria-hidden />
            </div>
            <ul className="-mx-2">
              {group.docs.map((doc) => (
                <DocumentRow key={doc.id} doc={doc} />
              ))}
            </ul>
          </section>
        ))}
        {hasMore ? (
          <LoadMoreSentinel
            onLoadMore={onLoadMore}
            isLoading={isLoading}
            loadedCount={documents.length}
          />
        ) : null}
      </div>
    </TooltipProvider>
  );
}

function DocumentRow({ doc }: { doc: ApiProjectDocument }) {
  const type = WEB_DOCUMENT_TYPES[doc.document_type] ?? doc.document_type;
  const Icon = documentIcon(doc.document_type);
  const href = isWebUrl(doc.source_url) ? doc.source_url : null;
  const content = (
    <>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-muted/40 text-muted-foreground">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-foreground">{type}</span>
        <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
          <span className="truncate">{doc.source_label}</span>
          <span aria-hidden>·</span>
          <span className="shrink-0">
            {doc.document_date ? formatSignalCalendarDate(doc.document_date) : 'Undated'}
          </span>
        </span>
      </span>
      {doc.has_extracted_facts ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:border-green-400/20 dark:bg-green-400/10 dark:text-green-300">
              <Quote className="h-3 w-3" aria-hidden />
              Facts cited
            </span>
          </TooltipTrigger>
          <TooltipContent>Project facts on this page cite this document</TooltipContent>
        </Tooltip>
      ) : null}
      {href ? (
        <ArrowUpRight
          className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/doc:opacity-100"
          aria-hidden
        />
      ) : null}
    </>
  );
  const rowClass = 'group/doc flex items-center gap-3 rounded-md px-2 py-2 text-sm';
  return (
    <li>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className={cn(
            rowClass,
            'transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none',
          )}
        >
          {content}
        </a>
      ) : (
        <div className={rowClass}>{content}</div>
      )}
    </li>
  );
}

/** Loads the next page as the bottom of the list scrolls into view; a focusable button covers keyboards. */
function LoadMoreSentinel({
  onLoadMore,
  isLoading,
  loadedCount,
}: {
  onLoadMore: () => void;
  isLoading: boolean;
  loadedCount: number;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const onLoadMoreRef = React.useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  // Re-observing after each page fires again if the sentinel is still on screen.
  // biome-ignore lint/correctness/useExhaustiveDependencies: loadedCount re-arms the observer after each page
  React.useEffect(() => {
    const node = ref.current;
    if (!node || isLoading || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMoreRef.current();
      },
      { rootMargin: '0px 0px 240px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isLoading, loadedCount]);

  return (
    <div ref={ref} data-load-more="">
      {isLoading ? (
        <ListSkeleton rows={2} plain />
      ) : (
        <button
          type="button"
          onClick={onLoadMore}
          className="sr-only focus:not-sr-only focus:w-full focus:rounded-md focus:border focus:py-2 focus:text-sm"
        >
          Load more
        </button>
      )}
    </div>
  );
}
